import { useMemo, useState } from "react";

import { Alert, Button, Row, Table } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ButtonFile } from "@/common/components/ButtonFile.tsx";
import ColoredMarker from "@/common/components/ColoredMarker.tsx";
import Loader from "@/common/components/Loader.tsx";
import UploadDocumentForm from "@/common/components/UploadDocumentForm.tsx";
import DwellingDiagnosticEpdForm from "@/modules/dwelling/components/diagnostic/DwellingDiagnosticEpdForm";
import DwellingDiagnosticSecurityForm from "@/modules/dwelling/components/diagnostic/DwellingDiagnosticSecurityForm";
import DwellingDiagnosticSurfaceForm from "@/modules/dwelling/components/diagnostic/DwellingDiagnosticSurfaceForm";
import { DiagnosticReferentialData } from "@/modules/dwelling/reducers";
import { ORDER_VIEW } from "@/modules/order/routes";
import {
  deleteFileServiceReport,
  updateDiagnostic,
  uploadFilesService,
} from "@/modules/order/services";
import {
  DiagnosticTypeValue,
  IService,
} from "@/modules/serviceOrdering/reducers";
import history from "@/routes/history.ts";
import { RootState } from "@/store";

const ServiceReportDiagnosticFrom = ({
  data,
  id,
}: {
  data: IService;
  id: string;
}) => {
  const queryClient = useQueryClient();
  const [dwellingDocuments, setDwellingDocuments] = useState<Array<any>>([]);
  const [existingDwellingDocuments, setExistingDwellingDocuments] = useState<
    Array<any>
  >(data?.report?.files || []);

  const referentials = useSelector(
    (state: RootState) => state.referentialReducer.referentials,
  );
  const diagnosticsSlugs = data?.diagnosticTypes?.map((diag) => diag.slug);

  const { control, handleSubmit, register } =
    useForm<DiagnosticReferentialData>({
      defaultValues: useMemo(() => data?.report?.serializedReport, [data]),
    });

  const mutationDiagnostic = useMutation({
    mutationFn: (diagnosticReferentialData: DiagnosticReferentialData) =>
      updateDiagnostic(id, diagnosticReferentialData),
    onError: (error) => {
      console.error(error);
      toast.error("Erreur lors de l'enregistrement du diagnostic");
    },
    onSuccess: async () => {
      toast.success("Diagnostic enregistré avec succès");
    },
  });

  const mutationDiagnosticAddDocuments = useMutation({
    mutationFn: () => uploadFilesService(data!.report!.id, dwellingDocuments),
    onError: (error) => {
      console.error(error);
      toast.dismiss("uploading-toast");
      toast.error("Erreur lors de l'enregistrement des fichiers");
    },
    onMutate: () => {
      toast.info("Fichier(s) en cours d'enregistrement", {
        toastId: "uploading-toast",
        isLoading: true,
      });
    },
    onSuccess() {
      toast.dismiss("uploading-toast");
      toast.success("Fichier(s) enregistré(s) avec succès");
    },
  });

  const mutationDiagnosticRemoveDocument = useMutation({
    mutationFn: (fileId: string) =>
      deleteFileServiceReport(data!.report!.id, fileId),
    onError: (error) => {
      console.error(error);
      toast.error("Erreur lors de la suppression du fichier");
    },
    onSuccess(_, fileId) {
      toast.success("Fichier supprimé avec succès");
      setExistingDwellingDocuments(
        existingDwellingDocuments.filter((doc) => doc.id !== fileId),
      );
    },
  });

  const onSubmit: SubmitHandler<DiagnosticReferentialData> = async (data) => {
    if (data.area) {
      data.area.living = Number(data.area.living);
      data.area.carrez = Number(data.area.carrez);
    }
    delete data?.EPD?.EPDLabel2020;
    await mutationDiagnostic.mutateAsync(data);
    if (dwellingDocuments.length > 0) {
      await mutationDiagnosticAddDocuments.mutateAsync();
    }
    await queryClient.invalidateQueries({ queryKey: ["service", id] });
    history.push(ORDER_VIEW.replace(":id", id));
  };

  if (
    mutationDiagnostic.isLoading ||
    mutationDiagnosticAddDocuments.isLoading
  ) {
    return <Loader />;
  }

  if (mutationDiagnostic.error) {
    return (
      <div className="bg-container">
        <Alert variant="danger">
          Erreur chargement des données : {mutationDiagnostic.error}
        </Alert>
      </div>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center">
        <h1 className="mb-3  mt-4 text-center text-lg-start d-lg-block">
          Diagnostic du bien
        </h1>
      </div>
      <form
        className="bg-container bg-container-form"
        onSubmit={handleSubmit(onSubmit)}
      >
        {diagnosticsSlugs?.includes(DiagnosticTypeValue.EDP) ||
        diagnosticsSlugs?.includes(DiagnosticTypeValue.EDPC) ? (
          <DwellingDiagnosticEpdForm control={control} />
        ) : undefined}

        <DwellingDiagnosticSecurityForm
          control={control}
          slugs={diagnosticsSlugs}
        />

        {diagnosticsSlugs?.includes(DiagnosticTypeValue.AREA) ? (
          <DwellingDiagnosticSurfaceForm register={register} />
        ) : undefined}

        <h2 className="fw-bold main-title my-4" style={{ fontSize: "24px" }}>
          Importer des documents
        </h2>
        <div className="mt-4 bg-container bg-container-form">
          {existingDwellingDocuments && existingDwellingDocuments.length > 0 ? (
            <Table borderless responsive className="">
              <tbody className="property-data-grid mt-3">
                {existingDwellingDocuments.map((file, index) => (
                  <tr className="datagrid-fields" key={index}>
                    <td className="text-center">
                      <ColoredMarker className="text-no-wrap" color="#000D5B">
                        {file.type.label}
                      </ColoredMarker>
                    </td>
                    <td className="text-center dark-purple-text">
                      <ButtonFile path={file.path} label="Voir le document" />
                    </td>
                    <td className="text-center">
                      <Button
                        className="btn-trash ms-4"
                        onClick={() =>
                          mutationDiagnosticRemoveDocument.mutate(
                            file.id as string,
                          )
                        }
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p className="p-no-property mt-3 ms-2">Aucun document</p>
          )}
          <Row>
            <UploadDocumentForm
              documents={dwellingDocuments}
              setDocuments={setDwellingDocuments}
              referentials={referentials}
              removeFile={(key) =>
                mutationDiagnosticRemoveDocument.mutate(key as string)
              }
            />
          </Row>
        </div>

        <div className="d-flex justify-content-end mt-3">
          <Button
            variant="success"
            style={{ padding: "15px 25px" }}
            type="submit"
          >
            Enregistrer
          </Button>
        </div>
      </form>
    </>
  );
};

export default ServiceReportDiagnosticFrom;
